import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { User, UserRole } from '../utils/types';

/**
 * خدمة فحص الإعداد الأولي للنظام
 */

/**
 * التحقق من وجود مدير في النظام
 * @returns Promise<boolean> - true إذا كان هناك مدير موجود
 */
export const hasAdminUsers = async (): Promise<boolean> => {
  try {
    if (!db) {
      console.warn('قاعدة البيانات غير متوفرة');
      return false;
    }

    console.log('التحقق من وجود مديرين في النظام...');
    
    // البحث عن مستخدمين بدور ADMIN
    const usersRef = collection(db, 'users');
    const adminQuery = query(
      usersRef, 
      where('role', '==', UserRole.ADMIN),
      where('isActive', '==', true)
    );
    
    const adminDocs = await getDocs(adminQuery);
    const adminCount = adminDocs.size;
    
    console.log(`تم العثور على ${adminCount} مدير نشط في النظام`);
    
    return adminCount > 0;
  } catch (error) {
    console.error('خطأ في فحص وجود المديرين:', error);
    return false;
  }
};

/**
 * الحصول على عدد المديرين النشطين في النظام
 * @returns Promise<number> - عدد المديرين النشطين
 */
export const getActiveAdminCount = async (): Promise<number> => {
  try {
    if (!db) {
      console.warn('قاعدة البيانات غير متوفرة');
      return 0;
    }

    const usersRef = collection(db, 'users');
    const adminQuery = query(
      usersRef, 
      where('role', '==', UserRole.ADMIN),
      where('isActive', '==', true)
    );
    
    const adminDocs = await getDocs(adminQuery);
    return adminDocs.size;
  } catch (error) {
    console.error('خطأ في حساب عدد المديرين:', error);
    return 0;
  }
};

/**
 * التحقق من إمكانية حذف مدير معين (منع حذف آخر مدير)
 * @param adminId معرف المدير المراد حذفه
 * @returns Promise<{canDelete: boolean, reason?: string}> - نتيجة التحقق
 */
export const canDeleteAdmin = async (adminId: string): Promise<{canDelete: boolean, reason?: string}> => {
  try {
    const adminCount = await getActiveAdminCount();
    
    if (adminCount <= 1) {
      return {
        canDelete: false,
        reason: 'لا يمكن حذف آخر مدير في النظام. يجب وجود مدير واحد على الأقل.'
      };
    }

    // التحقق من أن المستخدم المراد حذفه هو فعلاً مدير
    if (!db) {
      return {
        canDelete: false,
        reason: 'قاعدة البيانات غير متوفرة'
      };
    }

    const userDoc = await getDoc(doc(db, 'users', adminId));
    if (!userDoc.exists()) {
      return {
        canDelete: false,
        reason: 'المستخدم غير موجود'
      };
    }

    const userData = userDoc.data() as User;
    if (userData.role !== UserRole.ADMIN) {
      return {
        canDelete: true,
        reason: 'المستخدم ليس مديراً'
      };
    }

    return {
      canDelete: true
    };
  } catch (error) {
    console.error('خطأ في فحص إمكانية حذف المدير:', error);
    return {
      canDelete: false,
      reason: 'حدث خطأ أثناء التحقق من البيانات'
    };
  }
};

/**
 * التحقق من أن النظام يحتاج إلى إعداد أولي
 * @returns Promise<boolean> - true إذا كان النظام يحتاج إلى إعداد أولي
 */
export const needsInitialSetup = async (): Promise<boolean> => {
  try {
    const hasAdmin = await hasAdminUsers();
    return !hasAdmin;
  } catch (error) {
    console.error('خطأ في فحص الحاجة للإعداد الأولي:', error);
    // في حالة الخطأ، افترض أنه يحتاج إعداد أولي
    return true;
  }
};

/**
 * الحصول على قائمة بجميع المديرين النشطين
 * @returns Promise<User[]> - قائمة المديرين النشطين
 */
export const getActiveAdmins = async (): Promise<User[]> => {
  try {
    if (!db) {
      console.warn('قاعدة البيانات غير متوفرة');
      return [];
    }

    const usersRef = collection(db, 'users');
    const adminQuery = query(
      usersRef, 
      where('role', '==', UserRole.ADMIN),
      where('isActive', '==', true)
    );
    
    const adminDocs = await getDocs(adminQuery);
    const admins: User[] = [];
    
    adminDocs.forEach((doc) => {
      const userData = doc.data() as User;
      admins.push(userData);
    });

    return admins;
  } catch (error) {
    console.error('خطأ في الحصول على قائمة المديرين:', error);
    return [];
  }
};

/**
 * إنشاء نسخة احتياطية لبيانات مدير معين
 * @param adminId معرف المدير
 * @returns Promise<boolean> - true إذا تمت العملية بنجاح
 */
export const backupAdminData = async (adminId: string): Promise<boolean> => {
  try {
    if (!db) {
      console.warn('قاعدة البيانات غير متوفرة');
      return false;
    }

    // الحصول على بيانات المدير
    const userDoc = await getDoc(doc(db, 'users', adminId));
    if (!userDoc.exists()) {
      console.error('المدير غير موجود');
      return false;
    }

    const userData = userDoc.data() as User;
    
    // إنشاء نسخة احتياطية
    const backupData = {
      ...userData,
      backupDate: new Date(),
      backupType: 'admin_protection'
    };

    // حفظ النسخة الاحتياطية في مجموعة منفصلة
    const backupRef = doc(db, 'admin_backups', `${adminId}_${Date.now()}`);
    await setDoc(backupRef, backupData);

    console.log(`تم إنشاء نسخة احتياطية للمدير: ${userData.name}`);
    return true;
  } catch (error) {
    console.error('خطأ في إنشاء النسخة الاحتياطية:', error);
    return false;
  }
};

export default {
  hasAdminUsers,
  getActiveAdminCount,
  canDeleteAdmin,
  needsInitialSetup,
  getActiveAdmins,
  backupAdminData
};
